import { supportsRuntimeVersion } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'
import {
  ARMApplicationDetail, CloudHubSettings,
  PutSettingsApplication,
  RTFApplication,
  SettingsSimpleApplication
} from '../../../remote-api/models'
import { SettingsApplication } from '../Body/common/model'
import { SettingsCategory } from './SettingsConfigurations'
import ARMRemoteApi from '../../../remote-api/ARMRemoteApi'
import UIRemoteApi from '../../../remote-api/UIRemoteApi'

export class SettingsService {
  
  private readonly remoteAPI: ARMRemoteApi
  private readonly uiRemoteApi: UIRemoteApi
  private static readonly amEnabledProperties = [
    'anypoint.platform.config.analytics.agent.enabled'
  ]
  
  constructor(remoteAPI: ARMRemoteApi, uiRemoteApi: UIRemoteApi) {
    this.remoteAPI = remoteAPI
    this.uiRemoteApi = uiRemoteApi
  }
  
  public fetchApplications(orgId: string, envId: string): Promise<SettingsSimpleApplication[]> {
    return this.uiRemoteApi.fetchSettingsApplication(orgId, envId)
  }
  
  public fetchCloudHubSettings(orgId: string): Promise<CloudHubSettings> {
    return this.uiRemoteApi.cloudHubSettings('GET', orgId, undefined)
  }
  
  public postCloudHubSettings(orgId: string, enable: boolean): Promise<CloudHubSettings> {
    return this.uiRemoteApi.cloudHubSettings('POST', orgId, enable)
  }
  
  public fetchApplicationData(orgId: string, envId: string, appName: string): Promise<ARMApplicationDetail> {
    return this.remoteAPI.fetchApplicationData(orgId, envId, appName)
  }
  
  public fetchRTFData(orgId: string, envId: string, appName: string): Promise<RTFApplication> {
    return this.remoteAPI.fetchRTFData(orgId, envId, appName)
  }
  
  public fetchSettingsApplications(orgId: string, envId: string, apps: SettingsSimpleApplication[]): Promise<SettingsApplication[]> {
    return Promise.all(apps.map(app => this.fetchApplicationData(orgId, envId, app.applicationName)))
      .then(arrayOfAppDetails => [].concat.apply(
        [],
        arrayOfAppDetails.map(app => SettingsService.getSettingsAppFromARMApp(app))
      ) as SettingsApplication[])
  }
  
  public static isAMVersion(version: string) {
    return version.endsWith('AM')
  }
  
  public validateMuleVersionApps(apps: SettingsApplication[]): SettingsApplication[] {
    return apps.filter(app => {
      if (!app.muleVersion) return false
      return supportsRuntimeVersion(app.muleVersion.version) || SettingsService.isAMVersion(app.muleVersion.version)
    })
  }
  
  public static getSettingsAppFromARMApp(app: ARMApplicationDetail): SettingsApplication {
    return {
      name: app.domain,
      type: SettingsCategory.CloudHub,
      status: app.status,
      deploymentUpdateStatus: app.deploymentUpdateStatus,
      domain: app.domain,
      muleVersion: app.muleVersion,
      properties: app.properties,
      monitoringChecked: false
    }
  }
  
  public static getSettingsAppFromRTFApp(app: RTFApplication): SettingsApplication {
    return {
      name: app.name ? app.name : app.artifact ? app.artifact.name : app.id,
      type: SettingsCategory.RTF,
      status: app.application.status,
      deploymentUpdateStatus: undefined,
      domain: undefined,
      monitoringChecked: true,
      muleVersion: undefined,
      properties: undefined
    }
  }
  
  public static mergeMonitoringChecked(newApps?: SettingsApplication[],
                                       previousApps?: SettingsApplication[]): SettingsApplication[] {
    if (!newApps) return []
    if (!previousApps) {
      return newApps.map(app => ({...app, monitoringChecked: false}))
    } else {
      return newApps.map(newApp => {
        const previousApp = previousApps.find(app => app.name === newApp.name)
        if (previousApp) {
          return {...newApp, monitoringChecked: previousApp.monitoringChecked}
        } else {
          return {...newApp, monitoringChecked: false}
        }
      })
    }
  }
  
  public static isApplicationEnabled(app: SettingsApplication): boolean {
    if (app.muleVersion && SettingsService.isAMVersion(app.muleVersion.version)) return true
    for (let i = 0; i < this.amEnabledProperties.length; i++) {
      if (app.properties && !(this.amEnabledProperties[i] in app.properties) ||
        (app.properties && app.properties[this.amEnabledProperties[i]] !== 'true')
      ) {
        return false
      }
    }
    return true
  }
  
  public filterApplicationsByName(input: string, apps?: SettingsApplication[]): SettingsApplication[] {
    if (!apps) return []
    return apps.filter(app => app.name.includes(input))
  }
  
  public setApplicationAMEnabled(orgId: string, app: SettingsApplication, enabled: boolean, envId: string): Promise<ARMApplicationDetail> {
    for (let i = 0; i < SettingsService.amEnabledProperties.length; i++) {
      if (app.properties) app.properties[SettingsService.amEnabledProperties[i]] = String(enabled)
    }
    return this.putApplication(orgId, app, envId)
  }
  
  public putApplication(orgId: string, application: SettingsApplication, envId: string): Promise<ARMApplicationDetail> {
    const app = SettingsService.cloudHubSettingsAppToPutARMApp(application)
    return this.uiRemoteApi.putSettingsApplication(orgId, envId, app)
  }
  
  public static cloudHubSettingsAppToPutARMApp(app: SettingsApplication): PutSettingsApplication {
    return {
      applicationId: app.details &&  app.details.domain ? app.details.domain : '',
      applicationName: app.name ? app.name : '',
      properties: app.properties
    }
  }
  
}
